'use client';

/**
 * Plan Commercial Position's two ranked lists — vendors by decided
 * spend, products by decided spend. Each a thin shell slicing
 * usePlanCommercialView and rendering RankedListCard.
 *
 * @arch_component_type widget
 * @arch_domain canvas
 */

import { useMemo } from 'react';
import { useCardExport } from '@/lib/design/card-export';
import { useCardIdentity } from '@/lib/design/card-chrome';
import { usePlanCommercialView } from './use-plan-commercial-view';
import { RankedListCard, type RankedListRow } from './ranked-list-card';

export function ObeyaPlanCommercialVendorsCard() {
  const { contentKey } = useCardIdentity();
  const { data, queryKey, mcpId, strategy } = usePlanCommercialView();

  const rows: RankedListRow[] = (data?.vendors ?? []).map((v) => ({
    key: v.vendor,
    name: v.vendor,
    secondary: `${v.product_count} product${v.product_count !== 1 ? 's' : ''}${v.share_pct != null ? ` · ${v.share_pct}%` : ''}`,
    value: v.total,
  }));

  useCardExport(useMemo(() => ({
    content_key: contentKey ?? 'obeya_plan_commercial_vendors',
    status: (data ? 'loaded' : mcpId && strategy ? 'loading' : 'idle') as 'loaded' | 'loading' | 'idle',
    query_key: queryKey,
    text: `Vendors by decided spend: ${rows.length}`,
    json: data ? { vendors: data.vendors } : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [contentKey, data, mcpId, strategy, queryKey, rows.length]));

  if (!mcpId) return null;
  return <RankedListCard title="Vendors, by decided spend" rows={rows} emptyLabel="No decided lines yet" />;
}

export function ObeyaPlanCommercialProductsCard() {
  const { contentKey } = useCardIdentity();
  const { data, queryKey, mcpId, strategy } = usePlanCommercialView();

  const rows: RankedListRow[] = (data?.products ?? []).map((p) => ({
    key: p.product_name,
    name: p.product_name,
    secondary: `${p.quantity} units · ${p.vendors.join(', ')}`,
    value: p.total,
  }));

  useCardExport(useMemo(() => ({
    content_key: contentKey ?? 'obeya_plan_commercial_products',
    status: (data ? 'loaded' : mcpId && strategy ? 'loading' : 'idle') as 'loaded' | 'loading' | 'idle',
    query_key: queryKey,
    text: `Products by decided spend: ${rows.length}`,
    json: data ? { products: data.products } : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [contentKey, data, mcpId, strategy, queryKey, rows.length]));

  if (!mcpId) return null;
  return <RankedListCard title="Products, by decided spend" rows={rows} emptyLabel="No decided lines yet" />;
}
